import { useContext, useEffect, useRef } from "react";
import { skipToken } from "@reduxjs/toolkit/query";
import { BotFormContext } from "@shared/features/bots/BotForm/common/BotForm.context";
import { BotFormType } from "@shared/features/bots/BotForm/common/types";
import {
  SPOT_BOT_FUTURES_EXCHANGES,
  hasSpotSignals,
  shouldRecommendFuturesAsSpot,
} from "@shared/features/bots/BotForm/common/futuresAsSpot";
import { sharedLib } from "@shared/services/shared.service";
import { BotType } from "@shared/types";
import { isSymbol } from "@shared/types/utils";

const useFollowFuturesAsSpot = () => {
  const {
    account,
    subGroup,
    botType,
    botFormType,
    isChannelConfiguration,
    initialized,
    followFuturesAsSpot,
    setFollowFuturesAsSpot,
  } = useContext(BotFormContext)!;
  const exchange = isSymbol(account) ? undefined : account?.exchange;
  const isEligible =
    botType === BotType.SIGNAL &&
    !isChannelConfiguration &&
    botFormType !== BotFormType.BULK_CREATE &&
    botFormType !== BotFormType.BULK_EDIT &&
    !!exchange &&
    !!SPOT_BOT_FUTURES_EXCHANGES[exchange];
  const { currentData: group } =
    sharedLib.sharedApi.common.useGetGroupDataQuery(
      isEligible && subGroup ? { sub_group_id: subGroup.id } : skipToken
    );
  const defaultedContext = useRef<string | undefined>(undefined);
  const contextKey = `${subGroup?.id}:${
    isSymbol(account) ? "" : account?.client_id
  }`;
  const checked = followFuturesAsSpot === true;
  const recommended = shouldRecommendFuturesAsSpot(exchange, group);

  useEffect(() => {
    if (!initialized) return;
    if (!isEligible) {
      if (
        checked &&
        exchange &&
        botType === BotType.SIGNAL &&
        !isChannelConfiguration &&
        botFormType !== BotFormType.BULK_CREATE &&
        botFormType !== BotFormType.BULK_EDIT
      )
        setFollowFuturesAsSpot(false);
      defaultedContext.current = undefined;
      return;
    }
    if (
      !group ||
      botFormType === BotFormType.EDIT ||
      defaultedContext.current === contextKey
    )
      return;
    const preserveInitialValue =
      defaultedContext.current === undefined && checked;
    defaultedContext.current = contextKey;
    setFollowFuturesAsSpot(recommended || preserveInitialValue);
  }, [
    isEligible,
    initialized,
    contextKey,
    group,
    recommended,
    checked,
    exchange,
    botType,
    isChannelConfiguration,
    botFormType,
    setFollowFuturesAsSpot,
  ]);

  const requestChange = (enabled: boolean): void => {
    defaultedContext.current = contextKey;
    setFollowFuturesAsSpot(enabled);
  };
  return {
    isEligible,
    checked,
    showNotice: isEligible && checked,
    skipsSpotSignals: hasSpotSignals(exchange, group),
    requestChange,
  };
};

export type FollowFuturesAsSpotData = ReturnType<typeof useFollowFuturesAsSpot>;
export default useFollowFuturesAsSpot;
